import React from "react";
import { Link } from "react-router-dom";
import { Heart, Sparkles, ShoppingBag, Check, Trash2, Share2, Copy } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useSiteSettings } from "@/components/SiteSettings";
import { useNavigate } from "react-router-dom";
import { CATALOG } from "@/constants/testIds";
import { getActiveBadges } from "@/lib/badges";
import { useTaxonomy } from "@/context/TaxonomyContext";
import { proxify, proxifySrcSet } from "@/lib/imgProxy";
import { colorHex } from "@/lib/colorPalette";

const FIT_LABELS = {
  regular: "REGULAR FIT",
  oversized: "OVERSIZED FIT",
  slim: "SLIM FIT",
  relaxed: "RELAXED FIT",
  plus_size: "PLUS SIZE",
  boxy: "BOXY FIT",
  cropped: "CROPPED",
  tall: "TALL FIT",
  loose: "LOOSE FIT",
};

const DEFAULT_SIZES = ["S", "M", "L", "XL", "XXL"];

export default function ProductCard({ product, testIdPrefix = "catalog", activeColors = [], eager = false, referralCode = "" }) {
  const { toggleWishlist, wishlist, addToCart } = useCart();
  const { user } = useAuth();
  const site = useSiteSettings();
  const navigate = useNavigate();
  const isWished = wishlist?.product_ids?.includes(product.id);
  const price = product.sale_price || product.base_price;
  const hasDiscount = product.sale_price && product.sale_price < product.base_price;
  const discountPct = hasDiscount ? Math.round(((product.base_price - product.sale_price) / product.base_price) * 100) : 0;
  const sizes = (Array.isArray(product.available_sizes) && product.available_sizes.length)
    ? product.available_sizes
    : DEFAULT_SIZES;

  // If the user is filtering by colour, pick the matching swatch's image so
  // the card actually shows the requested colour variant (instead of the
  // default product image which may be a different colour).
  const swatchImage = (() => {
    if (!activeColors || activeColors.length === 0) return null;
    const swatches = product.color_swatches || [];
    for (const wanted of activeColors) {
      const w = wanted.toLowerCase();
      const match = swatches.find((s) => (s.name || "").toLowerCase() === w);
      if (match) {
        const imgs = match.images || {};
        const url = imgs.front || imgs.main || (Array.isArray(match.images) ? match.images[0] : null) ||
                    (typeof imgs === "string" ? imgs : null);
        if (url) return url;
      }
    }
    return null;
  })();
  const displayImage = swatchImage || product.images?.[0];

  const onHeart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return navigate("/login", { state: { from: window.location.pathname + window.location.search } });
    const wasWished = isWished;
    try {
      await toggleWishlist(product.id);
      if (wasWished) {
        toast.success("Removed from wishlist", { icon: <Trash2 className="w-4 h-4" /> });
      }
    } catch {
      toast.error("Couldn't update wishlist — try again");
    }
  };

  const quickAdd = async (e, size) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return navigate("/login", { state: { from: window.location.pathname + window.location.search } });
    try {
      await addToCart({ product_id: product.id, size, quantity: 1, color: product.available_colors?.[0] });
      toast.success(`Added size ${size} to cart`, { icon: <Check className="w-4 h-4" /> });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add to cart");
    }
  };

  const moveToCart = async (e, size) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return navigate("/login", { state: { from: window.location.pathname + window.location.search } });
    try {
      // If the product is customizable, size-picking must happen inside the
      // Studio — send the user there instead of quietly adding a blank tee.
      if (product.product_type === "customizable" || product.is_customizable) {
        navigate(`/custom?product_id=${product.id}`);
        return;
      }
      await addToCart({ product_id: product.id, size, quantity: 1, color: product.available_colors?.[0] });
      // Remove from wishlist AFTER cart insert succeeds so a failure doesn't
      // orphan the product (added neither to cart nor wishlist).
      if (isWished) {
        await toggleWishlist(product.id);
      }
      toast.success(`Moved to bag — size ${size}`, { icon: <Check className="w-4 h-4" /> });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't move to bag");
    }
  };

  // Wishlist-only "Share on WhatsApp" — builds a deep link to the product PDP
  // with the sender's ?ref=CODE attached so any signup / purchase converts to
  // referral credit. Falls back to native share on mobile when available.
  const shareOnWhatsApp = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const refQuery = referralCode ? `?ref=${encodeURIComponent(referralCode)}` : "";
    const url = `${origin}/p/${product.slug}${refQuery}`;
    const priceText = `₹${Math.round(price).toLocaleString("en-IN")}`;
    const msg = `Check this out — ${product.name} for ${priceText} on XYZPLUS ✨\n\n${referralCode ? "Use my link for a bonus credit on your first order:\n" : ""}${url}`;
    // Prefer native share on mobile — better UX than opening WhatsApp Web on a phone.
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share({
          title: product.name,
          text: msg,
          url,
        });
        return;
      } catch {
        // user cancelled or share failed — fall through to WhatsApp deep link
      }
    }
    const wa = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(wa, "_blank", "noopener,noreferrer");
  };

  // Copy the referral-tagged product link to clipboard — for pasting into
  // Instagram DMs, Twitter, Reddit, etc. where wa.me isn't the right channel.
  const copyProductLink = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const refQuery = referralCode ? `?ref=${encodeURIComponent(referralCode)}` : "";
    const url = `${origin}/p/${product.slug}${refQuery}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — paste anywhere", { icon: <Copy className="w-4 h-4" /> });
    } catch {
      toast.error("Couldn't copy — long-press the link on the PDP instead");
    }
  };

  const goCustomize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/custom?product_id=${product.id}`);
  };

  return (
    <Link
      to={`/p/${product.slug}`}
      data-testid={`${testIdPrefix}-product-${product.slug}`}
      className="group flex flex-col w-full h-full bg-white border border-zinc-200 hover:border-zinc-300 transition-[transform,box-shadow] duration-200 ease-out hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] hover:-translate-y-0.5 active:translate-y-0 active:shadow-md rounded-xl overflow-hidden"
    >
      <div className="relative overflow-hidden bg-zinc-50 aspect-[4/5] flex-shrink-0">
        <img
          src={proxify(displayImage || "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=600", 480)}
          srcSet={proxifySrcSet(displayImage, [320, 480, 640])}
          sizes="(max-width: 768px) 50vw, 25vw"
          alt={`${product.name}${product.category ? ` — ${product.category}` : ""} | XYZPLUS`}
          width="400"
          height="500"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          loading={eager ? "eager" : "lazy"}
          {...(eager ? { fetchPriority: "high" } : {})}
          decoding="async"
        />

        {/* Top-left badges. MOBILE strategy: minimal — only the discount as a
            micro-badge (Fit chip moved out of image to above title) so the
            model's face stays visible. DESKTOP: original stacked layout. */}
        <div className="absolute top-2 left-2 sm:top-3 sm:left-3 flex flex-col items-start gap-1.5 sm:gap-2 z-10">
          {hasDiscount && (
            <div
              className="bg-[#FF5722] text-white px-1.5 sm:px-2.5 py-0.5 sm:py-1 text-[10px] sm:text-xs font-bold tracking-tight sm:tracking-wider rounded-full shadow-sm leading-none"
              data-testid={`product-discount-badge-${product.slug}`}
            >
              -{discountPct}%<span className="hidden sm:inline"> OFF</span>
            </div>
          )}
          {product.fit_type && (
            <div
              className="hidden sm:block bg-white/95 backdrop-blur-sm text-zinc-900 px-2 sm:px-2.5 py-0.5 sm:py-1 text-xs font-semibold tracking-wider rounded-full shadow-sm border border-zinc-100"
              data-testid={`product-fit-badge-${product.slug}`}
            >
              {FIT_LABELS[product.fit_type] || product.fit_type.toUpperCase()}
            </div>
          )}
        </div>

        {/* Top-right wishlist heart — on the wishlist page it morphs into
            an explicit Remove/Trash affordance (red tint) since the product
            is already saved and the primary action is to remove it. Users
            reported the "filled heart = click to remove" pattern was not
            discoverable enough (Feb 2026 feedback). */}
        {testIdPrefix === "wishlist" ? (
          <button
            onClick={onHeart}
            aria-label="Remove from wishlist"
            title="Remove from wishlist"
            data-testid={`${testIdPrefix}-remove-${product.slug}`}
            className="absolute top-2 right-2 sm:top-3 sm:right-3 inline-flex items-center gap-1.5 h-9 px-3 bg-white border border-zinc-200 hover:border-red-500 hover:bg-red-500 hover:text-white text-zinc-600 hover:scale-105 transition-all rounded-full shadow-sm z-10"
          >
            <Trash2 size={14} />
            <span className="text-[11px] font-bold uppercase tracking-wider">Remove</span>
          </button>
        ) : (
          <button
            onClick={onHeart}
            aria-label="Wishlist"
            data-testid={`${testIdPrefix}-heart-${product.slug}`}
            className={`absolute top-2 right-2 sm:top-3 sm:right-3 w-9 h-9 bg-white border border-zinc-200 hover:border-[#FF5722] hover:scale-105 transition-all rounded-full flex items-center justify-center shadow-sm z-10 ${isWished ? "text-[#FF5722]" : "text-zinc-500"}`}
          >
            <Heart size={16} fill={isWished ? "currentColor" : "none"} />
          </button>
        )}

        {/* Bottom-left status badges (Featured / Best Sellers / New Arrivals
            / Trending). Anchored to image bottom-left so they don't collide
            with the top-left discount + fit chips or the top-right heart /
            customize chip. Max 2 visible — priority filtering in lib/badges.js. */}
        <ProductStatusBadges product={product} />
      </div>

      <div className="p-3 sm:p-4 md:p-5 flex-1 flex flex-col">
        {/* Mobile-only fit chip — moved out of the image to keep model faces
            visible. Hidden on sm+ where the chip stays overlaid on the image. */}
        {product.fit_type && (
          <div
            className="sm:hidden inline-block bg-zinc-100 text-zinc-700 px-1.5 py-0.5 mb-1.5 text-[9px] font-bold tracking-wider rounded-full uppercase"
            data-testid={`product-fit-badge-${product.slug}-mobile`}
          >
            {FIT_LABELS[product.fit_type] || product.fit_type.toUpperCase()}
          </div>
        )}
        <div className="text-overline text-zinc-500 mb-1" style={{fontSize: '0.55rem'}}>
          {product.product_type === "customizable" ? "Custom" : product.product_type === "digital_approval" ? "Bulk Order" : "Ready Stock"}
        </div>
        {/* Bewakoof-style brand prefix on every product name — asserts the
            XYZPLUS brand at every card so users associate the product with the
            house even in mixed-catalog placements (search, wishlist, PDP
            related products, etc.). Orange `+` mirrors the wordmark treatment
            used in the header/footer for visual consistency. Both size and
            visibility are admin-editable (Settings → PDP Typography + the
            per-product `hide_brand_prefix` toggle in the product editor). */}
        {(site?.pdp_show_brand_prefix_global !== false) && !product.hide_brand_prefix && (
          <div
            className="text-[#111111] mb-0.5 uppercase font-display font-bold"
            style={{
              fontSize: `${Math.max(9, Math.round((site?.pdp_brand_prefix_size_px || 14) * 0.65))}px`,
              letterSpacing: "0.02em",
            }}
            data-testid={`product-brand-prefix-${product.slug}`}
          >
            XYZ<span className="text-[#FF5722]">PLUS</span>
          </div>
        )}
        <h3
          className={`font-display leading-tight line-clamp-2 ${site?.pdp_product_name_bold === false ? "font-normal" : "font-bold"}`}
          style={{ fontSize: `${(site?.pdp_product_name_size_px || 30) * 0.55}px` }}
        >{product.name}</h3>

        {/* Inline Customizable chip — dark "Industrial Tag" treatment for
            maximum contrast on white cards. The orange Sparkles icon nested
            inside dark bg makes it pop without competing with the solid
            orange discount badge. */}
        {product.is_customizable && (
          <div className="mt-1.5">
            <span
              data-testid={`product-custom-badge-${product.slug}`}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-zinc-900 text-white text-[10px] font-bold uppercase tracking-widest rounded-full shadow-sm"
            >
              <Sparkles size={12} className="text-[#FF5722]" /> Customizable
            </span>
          </div>
        )}

        <div className="mt-auto pt-2 flex items-baseline gap-1.5 sm:gap-2 flex-wrap">
          <span className="font-bold text-sm sm:text-base">₹{Math.round(price).toLocaleString("en-IN")}</span>
          {hasDiscount && (
            <>
              <span className="text-[10px] sm:text-xs text-zinc-500 line-through">₹{Math.round(product.base_price).toLocaleString("en-IN")}</span>
              <span className="text-[10px] sm:text-xs font-black text-emerald-600 tracking-wide">{discountPct}% OFF</span>
            </>
          )}
        </div>
        {(() => {
          // Show the cheapest bulk-offer tier as a green pill — Bewakoof style.
          // Effective per-unit must be lower than the regular per-unit (price).
          const tiers = Array.isArray(product.bulk_offers) ? product.bulk_offers : [];
          const best = tiers
            .map((o) => {
              const mq = Number(o.min_qty) || 0;
              const tp = Number(o.total_price) || 0;
              if (mq < 2 || tp <= 0) return null;
              const perUnit = tp / mq;
              return perUnit < price ? { mq, tp, perUnit } : null;
            })
            .filter(Boolean)
            .sort((a, b) => a.perUnit - b.perUnit)[0];
          if (!best) return null;
          return (
            <div className="mt-2 inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full text-[10px] sm:text-[11px] font-bold text-emerald-700" data-testid={`product-bulk-offer-${product.slug}`}>
              <span className="inline-block w-2 h-2 bg-emerald-500 rounded-full" />
              Buy {best.mq} for ₹{Math.round(best.tp).toLocaleString("en-IN")}
            </div>
          );
        })()}
        {product.available_colors?.length > 0 && (() => {
          // Sort: active colours first so the user sees their selection visibly
          // highlighted at the start of the swatch row.
          const activeSet = new Set((activeColors || []).map((c) => c.toLowerCase()));
          const sorted = [...product.available_colors].sort((a, b) => {
            const aOn = activeSet.has((a || "").toLowerCase()) ? 0 : 1;
            const bOn = activeSet.has((b || "").toLowerCase()) ? 0 : 1;
            return aOn - bOn;
          });
          return (
            <div
              className="mt-2 sm:mt-3 flex items-center gap-1.5 sm:gap-2 flex-wrap"
              data-testid={`product-color-swatches-${product.slug}`}
            >
              {sorted.slice(0, 4).map((c) => {
                const on = activeSet.has((c || "").toLowerCase());
                return (
                  <span
                    key={c}
                    title={c}
                    data-testid={`product-color-dot-${product.slug}-${(c || '').toLowerCase().replace(/\s+/g, '-')}${on ? '-active' : ''}`}
                    className={`relative w-3 h-3 sm:w-3.5 sm:h-3.5 rounded-full flex-shrink-0 transition-all ${on ? "ring-2 ring-[#FF5722] ring-offset-1 border border-white scale-110" : "border border-zinc-300"}`}
                    style={{ backgroundColor: colorHex(c) }}
                  />
                );
              })}
              {sorted.length > 4 && (
                <span className="text-[10px] sm:text-xs text-zinc-500">+{sorted.length - 4}</span>
              )}
              {activeSet.size > 0 && !swatchImage && (
                <span
                  className="ml-1 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider text-[#FF5722] bg-orange-50 px-1.5 py-0.5 rounded-full"
                  data-testid={`product-color-filter-hint-${product.slug}`}
                >
                  Available in {Array.from(activeSet).join(", ")}
                </span>
              )}
            </div>
          );
        })()}

        {/* Wishlist-only "MOVE TO BAG" quick-action row — inline size chips
            so users can convert a saved item into a cart item without
            visiting the PDP. For customizable products the size chip button
            routes to the Studio (see moveToCart handler). */}
        {testIdPrefix === "wishlist" && (
          <div
            className="mt-3 pt-3 border-t border-dashed border-zinc-200"
            data-testid={`wishlist-move-row-${product.slug}`}
          >
            <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5">
              {product.is_customizable ? "Customize & bag" : "Move to bag — pick size"}
            </div>
            {product.is_customizable ? (
              <button
                type="button"
                onClick={(e) => moveToCart(e, sizes[0])}
                data-testid={`wishlist-move-customize-${product.slug}`}
                className="w-full inline-flex items-center justify-center gap-1.5 h-9 bg-[#111111] hover:bg-[#FF5722] text-white text-[11px] font-bold uppercase tracking-wider rounded-md transition-colors"
              >
                <Sparkles size={12} /> Design now
              </button>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {sizes.slice(0, 6).map((sz) => (
                  <button
                    key={sz}
                    type="button"
                    onClick={(e) => moveToCart(e, sz)}
                    data-testid={`wishlist-move-size-${product.slug}-${sz}`}
                    className="min-w-[36px] h-8 px-2 border border-zinc-300 hover:border-[#111111] hover:bg-[#111111] hover:text-white text-[11px] font-bold uppercase tracking-wider rounded-md transition-colors"
                  >
                    {sz}
                  </button>
                ))}
              </div>
            )}

            {/* Share this specific product on WhatsApp with the user's ref
                code auto-attached — turns every saved item into a viral hook.
                Only shown to signed-in users because we need a ref code.
                A small Copy icon sits alongside for pasting into IG/Twitter/DMs. */}
            {user && (
              <div className="mt-2 flex items-stretch gap-1.5">
                <button
                  type="button"
                  onClick={shareOnWhatsApp}
                  data-testid={`wishlist-share-product-${product.slug}`}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 h-8 border border-emerald-500 text-emerald-600 hover:bg-emerald-500 hover:text-white text-[10px] font-bold uppercase tracking-wider rounded-md transition-colors"
                >
                  <Share2 size={11} />
                  Share on WhatsApp
                  {referralCode ? <span className="text-[9px] opacity-70">· earn on signup</span> : null}
                </button>
                <button
                  type="button"
                  onClick={copyProductLink}
                  aria-label="Copy referral link"
                  title="Copy referral link"
                  data-testid={`wishlist-copy-link-${product.slug}`}
                  className="w-9 inline-flex items-center justify-center border border-zinc-300 text-zinc-600 hover:border-[#111111] hover:bg-[#111111] hover:text-white rounded-md transition-colors"
                >
                  <Copy size={12} />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </Link>
  );
}

export function colorMap(name) {
  // Deprecated: kept for backward compatibility with any external import.
  // Routes through the shared colorHex() so behaviour is consistent.
  return colorHex(name);
}


/** Renders up to 2 status badges (Featured > Best Sellers > New > Trending)
 *  at the bottom-left of the product image. Picks active ones based on
 *  product.collection_expiries so badges auto-disappear post-expiry. */
function ProductStatusBadges({ product }) {
  const tax = useTaxonomy();
  const badges = getActiveBadges(product, tax?.visual_settings);
  if (!badges.length) return null;
  return (
    <div
      className="absolute bottom-2 left-2 sm:bottom-3 sm:left-3 z-10 flex flex-row flex-wrap gap-1 md:gap-1.5 max-w-[95%]"
      data-testid={`product-status-badges-${product.slug}`}
    >
      {badges.map((b) => (
        <span
          key={b.id}
          data-testid={`product-badge-${b.id}-${product.slug}`}
          className={b.className}
          style={b.styleOverride || undefined}
        >
          <span aria-hidden>{b.icon}</span>
          <span>{b.label}</span>
          {b.shimmer && !b.styleOverride && <span className="badge-shimmer-overlay" aria-hidden />}
        </span>
      ))}
    </div>
  );
}
